import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MdCheck } from "react-icons/md";
import Layout from "../components/Layout";

const PAYPAL_PLAN_ID = "P-9BN062573R5793036NH6LR3Y";
const PAYPAL_CLIENT_ID =
  "AX4onFxFB4PttkGW7U9PZjPy-LHHrgxJWbQV2hwWV4gnJsIe_sghlf9JMYg8CS4GcN1KBjVaP0XqHmTT";
const PAYPAL_SDK_SRC = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription`;

function loadPayPalSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));
  if (window.paypal) return Promise.resolve(window.paypal);

  const existing = document.querySelector(`script[src="${PAYPAL_SDK_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window.paypal));
      existing.addEventListener("error", () => reject(new Error("PayPal SDK failed to load")));
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = PAYPAL_SDK_SRC;
    script.async = true;
    script.setAttribute("data-sdk-integration-source", "button-factory");
    script.onload = () => resolve(window.paypal);
    script.onerror = () => reject(new Error("PayPal SDK failed to load"));
    document.body.appendChild(script);
  });
}

const BUNDLE_INCLUDES = [
  "Custom-built website tailored to your brand",
  "Hyve+ subscription with priority booking tools",
  "Unified client management across web and Hyve",
  "Ongoing updates, hosting, and support",
];

export default function WebsiteHyvePlusBundle() {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    loadPayPalSdk()
      .then((paypal) => {
        if (cancelled || !paypal || !containerRef.current) return;
        containerRef.current.innerHTML = "";
        paypal
          .Buttons({
            style: {
              shape: "rect",
              color: "gold",
              layout: "vertical",
              label: "subscribe",
            },
            createSubscription: (_data, actions) =>
              actions.subscription.create({ plan_id: PAYPAL_PLAN_ID }),
            onApprove: (data) => {
              setStatus("approved");
              // eslint-disable-next-line no-alert
              alert(data.subscriptionID);
            },
            onError: (err) => {
              setStatus("error");
              setErrorMessage(err?.message || "Something went wrong with PayPal.");
            },
          })
          .render(containerRef.current)
          .then(() => {
            if (!cancelled) setStatus("ready");
          })
          .catch((err) => {
            if (cancelled) return;
            setStatus("error");
            setErrorMessage(err?.message || "Could not render PayPal button.");
          });
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus("error");
        setErrorMessage(err?.message || "Could not load PayPal.");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Layout>
      {/* Tell crawlers not to index this hidden page */}
      <meta name="robots" content="noindex, nofollow" />

      <section className="relative overflow-hidden bg-gradient-to-b from-purple-50 via-white to-white py-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="text-center"
          >
            <span className="inline-block bg-purple-50 text-purple-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              Exclusive offer
            </span>
            <h1 className="text-[40px] md:text-[56px] font-extrabold text-gray-900 leading-tight mb-4">
              Website &amp; <span className="hyve-text-gradient">Hyve+</span> Bundle
            </h1>
            <p className="text-[16px] md:text-[18px] text-gray-600 max-w-2xl mx-auto">
              Everything you need to run your business online — a stunning custom
              website paired with the full power of Hyve+, in one simple
              subscription.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
            className="mt-12 bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden"
          >
            <div className="hyve-gradient px-8 py-6">
              <p className="text-white/80 text-sm font-semibold uppercase tracking-wide">
                Subscription
              </p>
              <p className="text-white text-2xl font-bold mt-1">
                Website &amp; Hyve+ Bundle
              </p>
            </div>

            <div className="p-8">
              <ul className="space-y-3 mb-8">
                {BUNDLE_INCLUDES.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-50 flex items-center justify-center mt-0.5">
                      <MdCheck className="text-purple-600 w-4 h-4" />
                    </span>
                    <span className="text-gray-700 text-[16px]">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="border-t border-gray-100 pt-6">
                <p className="text-sm text-gray-600 mb-4">
                  Subscribe securely with PayPal. You can cancel anytime from your
                  PayPal account.
                </p>

                {status === "loading" && (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                  </div>
                )}

                {status === "error" && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 text-sm">
                    {errorMessage || "PayPal is unavailable right now. Please try again later."}
                  </div>
                )}

                {status === "approved" && (
                  <div className="bg-green-50 border border-green-200 text-green-700 rounded-md px-4 py-3 text-sm mb-4">
                    Thanks! Your subscription has been started. We'll be in touch
                    shortly to get your website project rolling.
                  </div>
                )}

                <div
                  id={`paypal-button-container-${PAYPAL_PLAN_ID}`}
                  ref={containerRef}
                />
              </div>
            </div>
          </motion.div>

          <p className="text-center text-xs text-gray-400 mt-8">
            This page is private. Please do not share without permission.
          </p>
        </div>
      </section>
    </Layout>
  );
}
